import { useDispatch, useSelector } from "react-redux";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import * as React from "react";
import {
  Box,
  ClickAwayListener,
  SxProps,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useTheme,
} from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import { MoreHoriz } from "@mui/icons-material";
import {
  deleteConversation,
  fetchExchanges,
  patchConversation,
  conversationActions,
  selectConversationAction,
} from "../../store/conversation.tsx";
import { styled } from "@mui/material/styles";
import PinIcon from "../icons/PinIcon.tsx";
import { Conversation } from "../../store/types.tsx";
import DeleteIcon from "../icons/DeleteIcon.tsx";
import RenameIcon from "../icons/RenameIcon.tsx";
import UnpinIcon from "../icons/UnpinIcon.tsx";
import TallyDialog from "../dialog/TallyDialog.tsx";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

interface ConversationDrawerSectionProps {
  conversations: Conversation[];
  title: string;
  toggleConversation: boolean;
  closeDrawer: () => void;
  onConversationSelected?: () => void;
}

interface PartialConversation {
  id: string;
  title: string | null;
}

interface StoreState {
  conversationState: {
    selectedConversation: string;
    selectedView: "conversation" | "dashboard" | "notifications";
  };
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.text.primary,
    maxWidth: 200,
    margin: 0,
    fontSize: theme.typography.pxToRem(14),
    fontFamily: theme.typography.fontFamily,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: "0px 4px 10px rgba(0,0,0,0.25)",
  },
}));

const ConversationDrawerSection: React.FC<ConversationDrawerSectionProps> = (
  props,
) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [openTooltip, setOpenTooltip] = React.useState<boolean>(false);
  const [editConversation, setEditConversation] =
    React.useState<PartialConversation | null>(null);
  const [isRenameDialogOpen, setRenameDialogOpen] =
    React.useState<boolean>(false);

  const handleRenameDialogOpen = (
    conversationId: string,
    conversationTitle: string | null,
  ) => {
    setOpenTooltip(false);
    setEditConversation({ id: conversationId, title: conversationTitle });
    setRenameDialogOpen(true);
  };

  // Close the dialog
  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
  };

  const handleRenameConversation = async (newConversationTitle: string) => {
    if (
      newConversationTitle !== undefined &&
      editConversation !== null &&
      newConversationTitle.trim() != ""
    ) {
      dispatch(
        patchConversation(
          editConversation.id,
          JSON.stringify({ title: newConversationTitle }),
        ) as any,
      );
      handleRenameDialogClose();
    } else {
    }
  };

  const htmlTooltipListItemProps: SxProps = [
    {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.pxToRem(14),
      minHeight: 20,
      paddingY: theme.spacing(1),
    },
    {
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.main,
        borderRadius: theme.shape.borderRadius / 2,
      },
    },
  ];

  const { selectedConversation, selectedView } = useSelector(
    (state: StoreState) => state.conversationState,
  );

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };

  const handleTooltipOpen = () => {
    if (openTooltip) {
      setOpenTooltip(false);
    } else {
      setOpenTooltip(true);
    }
  };

  const togglePinConversation = async (
    id: string,
    is_pinned_value: boolean,
  ) => {
    dispatch(
      patchConversation(
        id,
        JSON.stringify({ is_pinned: is_pinned_value }),
      ) as any,
    );
  };

  const selectConversation = async (id: string) => {
    props.closeDrawer();
    dispatch(conversationActions.setSelectedView("conversation"));
    dispatch(selectConversationAction(id) as any);
    dispatch(fetchExchanges(id) as any);
    props.onConversationSelected?.();
  };

  const deleteConversationAction = async (id: string) => {
    dispatch(deleteConversation(id) as any);
  };

  return (
    props.conversations.length > 0 && (
      <div className="conversation-drawer-section">
        <h4>{props.title}</h4>
        <List>
          {props.conversations.map((conversation) => (
            <ListItem
              key={conversation.id}
              disablePadding
              sx={{
                borderRadius: "25px",
                transition: "all 0.2s ease",
                overflow: "visible",
              }}
            >
              <Box
                className="conversation-section-full-width"
                sx={[
                  {
                    ml: "-10px",
                    mr: "-10px",
                    borderRadius: "25px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "calc(100% + 6px)", // compensate for negative margins
                  },
                  selectedView === "conversation" &&
                    selectedConversation === conversation.id && {
                      backgroundColor: "rgba(0, 0, 0, 0.20)",
                      color: theme.palette.common.black,
                      borderRadius: "14px",
                    },
                ]}
              >
                <ListItemButton
                  disableRipple
                  onClick={() => selectConversation(conversation.id)}
                  sx={{
                    flex: 1,
                    borderRadius: "25px",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <ListItemText
                    primary={conversation.title}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      "& .MuiListItemText-primary": {
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </ListItemButton>
                {selectedView === "conversation" &&
                  selectedConversation === conversation.id && (
                    <ClickAwayListener onClickAway={handleTooltipClose}>
                      {/*
                    DO NOT REMOVE this DIV. It will break the behavior on mobile screens or have unexpected behavior.
                    The reason is that in order for the tooltip to work properly, because the child of a Material-UI Tooltip must be able to hold a ref.
                    https://stackoverflow.com/questions/57527896/material-ui-tooltip-doesnt-display-on-custom-component-despite-spreading-props
                    */}
                      <Box>
                        <HtmlTooltip
                          onClose={handleTooltipClose}
                          title={
                            <React.Fragment>
                              <List
                                sx={{
                                  margin: 0,
                                  padding: 0,
                                }}
                              >
                                <ListItemButton
                                  sx={htmlTooltipListItemProps}
                                  onClick={() =>
                                    togglePinConversation(
                                      conversation.id,
                                      props.toggleConversation,
                                    )
                                  }
                                >
                                  {props.toggleConversation ? (
                                    <PinIcon></PinIcon>
                                  ) : (
                                    <UnpinIcon></UnpinIcon>
                                  )}

                                  {props.toggleConversation
                                    ? i18n._(
                                        msg({
                                          context:
                                            "Button title of the pin button",
                                          message: "Pin",
                                        }),
                                      )
                                    : i18n._(
                                        msg({
                                          context:
                                            "Button title of the unpin button",
                                          message: "Unpin",
                                        }),
                                      )}
                                </ListItemButton>
                                <ListItemButton
                                  sx={htmlTooltipListItemProps}
                                  onClick={() =>
                                    handleRenameDialogOpen(
                                      conversation.id,
                                      conversation.title,
                                    )
                                  }
                                >
                                  <RenameIcon />
                                  <Trans
                                    context={
                                      "Button title of the rename button."
                                    }
                                  >
                                    Rename
                                  </Trans>
                                </ListItemButton>
                                <ListItemButton
                                  sx={htmlTooltipListItemProps}
                                  onClick={() =>
                                    deleteConversationAction(conversation.id)
                                  }
                                >
                                  <DeleteIcon />
                                  <Trans
                                    context={
                                      "Button title of the delete button."
                                    }
                                  >
                                    Delete
                                  </Trans>
                                </ListItemButton>
                              </List>
                            </React.Fragment>
                          }
                          placement="bottom-start"
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          open={openTooltip}
                        >
                          <ListItemButton
                            onClick={handleTooltipOpen}
                            sx={{
                              float: "right",
                              width: "1.5rem",
                              display: "flex",
                              ".MuiListItem-root:hover &": {
                                display: "flex",
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={[
                                {
                                  color: theme.palette.primary.main,
                                },
                              ]}
                            >
                              <MoreHoriz />
                            </ListItemIcon>
                          </ListItemButton>
                        </HtmlTooltip>
                      </Box>
                    </ClickAwayListener>
                  )}
              </Box>
            </ListItem>
          ))}
        </List>
        <TallyDialog
          open={isRenameDialogOpen}
          title={i18n._(
            msg({
              context: "Button title of the rename conversation button",
              message: "Rename conversation",
            }),
          )}
          subtitle={i18n._(
            msg({
              context:
                "Span above the title input explaining the limitation of the field bellow.",
              message: "Field cannot be longer than 100 characters",
            }),
          )}
          submitTitle={i18n._(
            msg({
              context: "Button title of the rename conversation button",
              message: "Rename",
            }),
          )}
          onSubmit={handleRenameConversation}
          initialInputValue={editConversation?.title ?? ""}
          handleDialogClose={handleRenameDialogClose}
        />
      </div>
    )
  );
};

export default ConversationDrawerSection;
